import { Container, Section, Skeleton } from '@resolute/ui';
import { MapPin } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useFeaturedProperties } from '../api';

import { Reveal } from '@/components/motion/reveal';
import { useFilterOptions } from '@/features/search/api';
import { buildSearchUrl } from '@/features/search/search-url';

/**
 * Localities that actually have inventory, read off the live listings.
 *
 * This replaces a hand-written list of 12 micro-markets — none of which were
 * checked against the inventory, and most of which had nothing to show. Reusing
 * the same `useFeaturedProperties(12)` query the FeaturedProperties section
 * above already issues means this costs no extra request: both read the one
 * cached entry.
 *
 * The city name is skipped: the API falls back to it when a project has no area
 * (`locality = area?.name ?? city.name`), and "Ahmedabad" is not a locality
 * within Ahmedabad.
 */
function useInventoryLocalities(city: string): { areas: string[]; isLoading: boolean } {
  const { data, isLoading } = useFeaturedProperties(12);
  const areas = useMemo(() => {
    const seen = new Set<string>();
    for (const p of data ?? []) {
      const locality = p.location.locality?.trim();
      if (locality && locality.toLowerCase() !== city.toLowerCase()) seen.add(locality);
    }
    return [...seen];
  }, [data, city]);
  return { areas, isLoading };
}

/**
 * Placeholder chips shown while the listings load.
 *
 * The count is chosen so the grid occupies the SAME number of rows as the real
 * list will at every breakpoint (2 / 3 / auto-fill columns), which is what keeps
 * the section from resizing when data lands. Without this the whole section
 * appeared out of nowhere and pushed 247px of page down — by far the largest
 * layout shift on the home page.
 */
const PLACEHOLDER_CHIPS = 6;

/**
 * Turns a locality name into the search URL for that locality.
 *
 * The `areas` filter takes CPzone's numeric area IDs, not names, so the chip
 * label has to be resolved against `/filter-options` — whose labels carry the
 * city in brackets ("South Bopal (Ahmedabad)") while a listing's `locality` is
 * the bare name. Stripping that suffix is what makes the two line up.
 *
 * When a locality has no matching area (the filter list and the listings come
 * from different CPzone endpoints, so this can happen), the chip falls back to
 * a plain text search for the name — still useful, never a dead link.
 */
function useAreaHref(): (locality: string) => string {
  const { data } = useFilterOptions();

  const idByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of data?.areas ?? []) {
      const bare = opt.label.replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase();
      // First match wins: CPzone repeats some locality names across cities, and
      // the earlier entry is the lower-ordered (more prominent) one.
      if (bare && !map.has(bare)) map.set(bare, opt.value);
    }
    return map;
  }, [data]);

  return (locality: string) => {
    const id = idByName.get(locality.trim().toLowerCase());
    return buildSearchUrl('/inventory', id ? { areas: [id] } : { q: locality });
  };
}

export function PopularAreas() {
  const { areas, isLoading } = useInventoryLocalities('Ahmedabad');
  const hrefFor = useAreaHref();

  // Keep the section mounted while loading so its space is already reserved.
  // Only drop it once we know there is genuinely nothing to show.
  if (!isLoading && areas.length === 0) return null;

  return (
    <Section spacing="md" className="border-t border-border bg-muted/30">
      <Container>
        <Reveal>
          <p className="text-sm font-medium uppercase tracking-widest text-secondary">Explore</p>
          <h2 className="mt-2 font-display text-display-sm font-semibold tracking-tight">
            Popular areas in Ahmedabad
          </h2>
        </Reveal>

        <Reveal
          delay={0.08}
          className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fill,minmax(12.5rem,1fr))]"
        >
          {isLoading
            ? Array.from({ length: PLACEHOLDER_CHIPS }, (_, i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  // Same box as a real chip (border + px-3 py-1.5 + text-sm line
                  // height) so swapping one for the other changes nothing but
                  // the contents.
                  className="flex w-full items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 shadow-sm"
                >
                  <Skeleton className="size-3.5 shrink-0 rounded-sm" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ))
            : areas.map((area) => (
                // A link, not a button: these navigate, so they should be
                // middle-clickable, openable in a new tab, and crawlable. They
                // were `<button>` with no handler at all, so clicking one did
                // nothing.
                <Link
                  key={area}
                  to={hrefFor(area)}
                  aria-label={`Browse properties in ${area}`}
                  className="group flex w-full items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow-sm transition-colors duration-fast ease-out-expo hover:border-primary/50 hover:text-primary"
                >
                  <MapPin
                    className="size-3.5 shrink-0 text-muted-foreground transition-colors duration-fast ease-out-expo group-hover:text-primary"
                    aria-hidden="true"
                  />
                  <span className="truncate">{area}</span>
                </Link>
              ))}
        </Reveal>
      </Container>
    </Section>
  );
}
